import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import VideoLibraryOutlinedIcon from '@mui/icons-material/VideoLibraryOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { _Api } from '../api/_call';
import { error, loading, success, Updateloading } from '../common/helpers/toastify';
import { useAiStatus } from '../common/helpers/useAiStatus';
import { GetCountrygraph } from '../../admin/common/Commongraphql';
import './CreateColorStone.scss';

const CREATE_COLOR_STONE_API = 'api/v1/Inventory/colorStone/createColorStone';
const CREATE_MASTERS_API = 'api/v1/Inventory/colorStone/getCreateMasters';
const DIAMOND_CREATE_MASTERS_API = 'api/v1/Inventory/diamond/getCreateMasters';
const EDIT_COLOR_STONE_API = 'api/v1/Inventory/colorStone/editColorStone';
const COLOR_STONE_DETAILS_API = 'api/v1/Inventory/colorStone/getDetails';
const AI_DESCRIPTION_API = 'api/v1/APP/ai/colorstone-description';
const AI_NAME_API = 'api/v1/APP/ai/colorstone-name';

const MAX_VISIBLE_TABS = 10;

const CATEGORY_OPTIONS = ['Loose stone', 'Natural stone', 'Precious stone'];
const DEFAULT_STATUS_OPTIONS = [{ value: '1', label: 'Active' }];
const DEFAULT_MEDIA = {
  image: { extensions: ['jpg', 'jpeg', 'png'], maxFiles: 10, maxSizeMB: 10 },
  video: { extensions: ['mp4', 'mov', 'gif'], maxFiles: 5, maxSizeMB: 50 },
};

const fileExtension = (file) => (file?.name?.split('.').pop() || '').toLowerCase();

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const emptyCertificate = () => ({ lab: '', number: '', url: '', file: null, showUrl: false });

const VIDEO_EXTENSIONS = ['mp4', 'webm', 'ogg', 'mov', 'm4v'];

const urlExtension = (url) => (String(url).split('?')[0].split('.').pop() || '').toLowerCase();

const resolveOption = (list, raw) => {
  if (raw === undefined || raw === null || raw === '') return '';
  const candidates = typeof raw === 'object' ? [raw._id, raw.value, raw.name, raw.label] : [raw];
  const keys = candidates.filter(Boolean).map((c) => String(c).toLowerCase());
  const match = (list || []).find(
    (o) => keys.includes(String(o.value).toLowerCase()) || keys.includes(String(o.label).toLowerCase()),
  );
  return match ? match.value : String(candidates.find(Boolean) ?? '');
};

const existingMedia = (url, isVideo) => ({
  file: null,
  existing: url,
  preview: url,
  isVideo,
  isGif: urlExtension(url) === 'gif',
});

const existingCertificate = (cert, labOptions) => {
  const image = cert?.image || '';
  const url = cert?.CertificationUrl || '';
  return {
    lab: resolveOption(labOptions, cert?.GradedBy),
    number: String(cert?.Certification ?? ''),
    url,
    file: image ? { name: image.split('/').pop(), existing: image } : null,
    preview: image && urlExtension(image) !== 'pdf' ? image : '',
    showUrl: Boolean(url),
  };
};

const emptyItem = () => ({
  productId: '',
  productName: '',
  category: '',
  stoneName: '',
  description: '',
  shape: '',
  size: '',
  color: '',
  clarity: '',
  cut: '',
  origin: '',
  treatment: '',
  pcs: '',
  weight: '',
  weightUnit: 'Cts',
  price: '',
  priceUnit: 'Cts',
  tagPrice: '',
  lotNo: '',
  certificates: [emptyCertificate()],
  status: '1',
  channelPos: false,
  channelEcommerce: false,
  media: [],
  defaultMedia: '',
  stockId: '',
  location: '',
});

const REQUIRED_FIELDS = [
  'productId',
  'productName',
  'category',
  'shape',
  'color',
  'clarity',
  'cut',
  'origin',
  'pcs',
  'weight',
  'price',
  'stockId',
  'location',
];

const priceNumber = (value) => {
  if (value === undefined || value === null || value === '') return '';
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  const raw = String(value).trim().replace(/\s/g, '');
  if (/^-?\d+(\.\d+)?$/.test(raw)) return String(Number(raw));
  const dots = (raw.match(/\./g) || []).length;
  const commas = (raw.match(/,/g) || []).length;
  let normalized = raw;
  if (dots && commas) {
    const decimalChar = raw.lastIndexOf('.') > raw.lastIndexOf(',') ? '.' : ',';
    const thousandChar = decimalChar === '.' ? ',' : '.';
    normalized = raw.split(thousandChar).join('').replace(decimalChar, '.');
  } else if (dots > 1) {
    normalized = raw.split('.').join('');
  } else if (commas > 1) {
    normalized = raw.split(',').join('');
  } else if (commas === 1) {
    normalized = raw.replace(',', '.');
  }
  const number = Number(normalized.replace(/[^\d.-]/g, ''));
  return Number.isFinite(number) ? String(number) : '';
};

const calcAmount = (item) => {
  const price = Number(item.price) || 0;
  const qty = item.priceUnit === 'Pcs' ? Number(item.pcs) || 0 : Number(item.weight) || 0;
  return price * qty;
};

const isBlank = (value) =>
  Array.isArray(value) ? value.length === 0 : String(value ?? '').trim() === '';

const FIELD_LABELS = {
  productId: 'Product ID',
  productName: 'Product Name',
  category: 'Category',
  stoneName: 'Stone Name',
  shape: 'Shape',
  color: 'Color',
  clarity: 'Clarity',
  cut: 'Cut',
  origin: 'Origin',
  treatment: 'Treatment',
  pcs: 'Pcs',
  weight: 'Weight',
  price: 'Price',
  stockId: 'Stock ID',
  location: 'Location',
};

const requiredMessage = (label, t) => `${t(label)} ${t('is required')}`;

const getItemErrors = (item, t) => {
  const fields = {};
  REQUIRED_FIELDS.forEach((key) => {
    if (isBlank(item[key])) fields[key] = requiredMessage(FIELD_LABELS[key] || key, t);
  });
  const certificates = item.certificates.map((c) => {
    const certErrors = {};
    if (!c.lab) certErrors.lab = requiredMessage('Certificate', t);
    if (isBlank(c.number)) certErrors.number = requiredMessage('Certificate No.', t);
    return certErrors;
  });
  const hasErrors =
    Object.keys(fields).length > 0 || certificates.some((c) => Object.keys(c).length > 0);
  return { fields, certificates, hasErrors };
};

const isItemValid = (item) => !getItemErrors(item, (s) => s).hasErrors;

const isItemTouched = (item) => {
  const blank = emptyItem();
  const textKeys = [...REQUIRED_FIELDS, 'description', 'tagPrice', 'lotNo'];
  return (
    textKeys.some((key) => !isBlank(item[key]) && item[key] !== blank[key]) ||
    item.channelPos ||
    item.channelEcommerce ||
    item.media.length > 0 ||
    item.certificates.some((c) => c.lab || !isBlank(c.number) || !isBlank(c.url) || c.file)
  );
};

const apiErrorMessage = (res) => {
  if (res?.message) return res.message;
  const first = Object.values(res?.errors || {})[0];
  return Array.isArray(first) ? first[0] : first?.msg || first;
};

const snapshotItems = (list) =>
  JSON.stringify(list, (key, value) =>
    value instanceof File ? `${value.name}:${value.size}:${value.lastModified}` : value,
  );

const TextField = ({ label, required, value, onChange, type = 'text', suffix, icon, disabled, alwaysFloat, error: errorText ,style={}}) => (
  <label
    className={`dc-field ${value !== '' && value !== undefined ? 'has-value' : ''} ${alwaysFloat ? 'float' : ''} ${disabled ? 'is-disabled' : ''} ${errorText ? 'has-error' : ''}`}
  >
    {icon && <span className="dc-field-icon">{icon}</span>}
    <input
      type={type}
      value={value}
      placeholder=" "
      disabled={disabled}
      onChange={(e) => onChange?.(e.target.value)}
    />
    <span className="dc-field-label" style={style}>
      {label}
      {required && <span className="dc-required">*</span>}
    </span>
    {suffix && <span className="dc-field-suffix">{suffix}</span>}
  </label>
);

const SelectField = ({ label, required, value, onChange, options, compact, openUp, error: errorText, multiple }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const listboxId = useId();
  const searchable = !compact;

  const selectedValues = multiple ? (Array.isArray(value) ? value.map(String) : []) : [];
  const isSelected = (option) =>
    multiple ? selectedValues.includes(String(option.value)) : String(option.value) === String(value);
  const selected = multiple
    ? selectedValues.length
      ? { label: options.filter(isSelected).map((o) => o.label).join(', ') }
      : undefined
    : options.find((o) => String(o.value) === String(value));
  const availableOptions = multiple ? options.filter((o) => !isSelected(o)) : options;
  const visibleOptions = searchable && query
    ? availableOptions.filter((o) => String(o.label).toLowerCase().includes(query.toLowerCase()))
    : availableOptions;
  const selectedOptions = multiple
    ? selectedValues
      .map((v) => options.find((o) => String(o.value) === v))
      .filter(Boolean)
    : [];

  const removeValue = (optionValue) =>
    onChange((Array.isArray(value) ? value : []).filter((v) => String(v) !== String(optionValue)));

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const openMenu = () => {
    setOpen(true);
    setQuery('');
    setHighlight(Math.max(0, options.findIndex((o) => String(o.value) === String(value))));
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const choose = (option) => {
    if (multiple) {
      const key = String(option.value);
      const current = Array.isArray(value) ? value : [];
      if (!selectedValues.includes(key)) onChange([...current, option.value]);
      setQuery('');
      setOpen(false);
      return;
    }
    onChange(option.value);
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (!open) {
      if (['Enter', ' ', 'ArrowDown'].includes(e.key)) {
        e.preventDefault();
        openMenu();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, visibleOptions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (visibleOptions[highlight]) choose(visibleOptions[highlight]);
    } else if (e.key === 'Escape' || e.key === 'Tab') {
      setOpen(false);
    }
  };

  return (
    <div
      ref={rootRef}
      className={`dc-field dc-select ${open ? 'is-open' : ''} ${selected || open || compact ? 'float' : ''} ${compact ? 'compact' : ''} ${openUp ? 'open-up' : ''} ${errorText ? 'has-error' : ''} ${multiple ? 'is-multi' : ''}`}
      tabIndex={open && searchable ? -1 : 0}
      role="combobox"
      aria-expanded={open}
      aria-controls={listboxId}
      onClick={() => (open ? !searchable && setOpen(false) : openMenu())}
      onKeyDown={handleKeyDown}
    >
      {multiple ? (
        <div className="dc-select-chips">
          {selectedOptions.map((option) => (
            <span className="dc-select-chip" key={option.value}>
              <span className="dc-select-chip-label">{option.label}</span>
              <button
                type="button"
                aria-label={t('Remove')}
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => {
                  e.stopPropagation();
                  removeValue(option.value);
                }}
              >
                <CloseIcon fontSize="inherit" />
              </button>
            </span>
          ))}
          {open && searchable && (
            <input
              ref={inputRef}
              className="dc-select-search"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setHighlight(0);
              }}
            />
          )}
        </div>
      ) : open && searchable ? (
        <input
          ref={inputRef}
          className="dc-select-search"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setHighlight(0);
          }}
        />
      ) : (
        <span className="dc-select-value">
          {selected?.label ?? ''}
        </span>
      )}
      <span className="dc-field-label">
        {label}
        {required && <span className="dc-required">*</span>}
      </span>
      {open && searchable ? (
        <SearchOutlinedIcon className="dc-select-icon" fontSize="small" />
      ) : (
        <ArrowDropDownIcon className={`dc-select-icon caret ${open ? 'up' : ''}`} />
      )}
      {open && (
        <ul
          id={listboxId}
          className="dc-select-menu"
          role="listbox"
          aria-multiselectable={multiple || undefined}
          onClick={(e) => e.stopPropagation()}
        >
          {visibleOptions.length ? (
            visibleOptions.map((option, index) => (
              <li
                key={option.value}
                role="option"
                aria-selected={isSelected(option)}
                className={`${index === highlight ? 'highlight' : ''} ${isSelected(option) ? 'selected' : ''}`}
                onMouseEnter={() => setHighlight(index)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(option)}
              >
                
                {option.label}
              </li>
            ))
          ) : (
            <li className="empty">{t('No options')}</li>
          )}
        </ul>
      )}
    </div>
  );
};

const CreateColorStone = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const editId = location.state?.editColorStoneId || '';
  const isEdit = Boolean(editId);
  const [editLoading, setEditLoading] = useState(isEdit);
  const [masters, setMasters] = useState({});
  const [mastersLoaded, setMastersLoaded] = useState(false);
  const [countrynamearr, setCountrynamearr] = useState([]);
  const [items, setItems] = useState([emptyItem()]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const originOptions = useMemo(
    () => countrynamearr.map((c) => ({ value: c.name, label: c.name })),
    [countrynamearr],
  );
  const [moreTabsOpen, setMoreTabsOpen] = useState(false);
  const [generating, setGenerating] = useState(null);
  const aiInactive = useAiStatus(_Api) === false;
  
  const moreTabsRef = useRef(null);
  const mediaInputRef = useRef(null);
  const initialSnapshotRef = useRef(null);
  const [discardOpen, setDiscardOpen] = useState(false);

  useEffect(() => {
    if (!discardOpen) return undefined;
    const handleKey = (e) => e.key === 'Escape' && setDiscardOpen(false);
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [discardOpen]);

  useEffect(() => {
    if (!moreTabsOpen) return undefined;
    const handleClickOutside = (e) => {
      if (moreTabsRef.current && !moreTabsRef.current.contains(e.target)) setMoreTabsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [moreTabsOpen]);
  const certFileRefs = useRef({});

  useEffect(() => {
    const loadMasters = async () => {
      try {
        const res = await _Api({}, CREATE_MASTERS_API);
        let data = res?.code === 200 ? res.data || {} : {};

        const needsMediaFallback = !data?.media?.image?.extensions?.length || !data?.media?.video?.extensions?.length;
        const needsStatusFallback = !data?.Status?.length;
        const sharedKeys = [
          'Shape', 'Colour', 'Clarity', 'Cut', 'Location',
          'GradedBy', 'WUnit', 'Unit', 'PriceUnit', 'Size', 'Category',
        ];
        const needsSharedFallback = sharedKeys.some((key) => !data?.[key]?.length);
        if (needsMediaFallback || needsStatusFallback || needsSharedFallback) {
          try {
            const diamondRes = await _Api({}, DIAMOND_CREATE_MASTERS_API);
            if (diamondRes?.code === 200 && diamondRes?.data) {
              const d = diamondRes.data;
              data = { ...data };
              if (needsMediaFallback && d.media) data.media = d.media;
              if (needsStatusFallback && d.Status?.length) data.Status = d.Status;
              sharedKeys.forEach((key) => {
                if (!data[key]?.length && d[key]?.length) data[key] = d[key];
              });
            }
          } catch (diamondErr) {
            console.log(diamondErr, 'diamond masters fallback');
          }
        }

        if (res?.code === 200 || data.media || data.Status) {
          setMasters(data);
          const defaultStatus = data.Status?.[0]?.value ?? DEFAULT_STATUS_OPTIONS[0].value;
          setItems((prev) =>
            prev.map((it) => ({
              ...it,
              status: defaultStatus !== undefined ? String(defaultStatus) : it.status || DEFAULT_STATUS_OPTIONS[0].value,
              weightUnit: data.Unit?.includes(it.weightUnit) ? it.weightUnit : data.Unit?.[0] || it.weightUnit,
              priceUnit: data.PriceUnit?.includes(it.priceUnit)
                ? it.priceUnit
                : data.PriceUnit?.[0] || it.priceUnit,
            })),
          );
        } else {
          error(res?.message || t('Please Wait or try again later!!'));
        }
      } catch (err) {
        console.log(err, 'err');
      } finally {
        setMastersLoaded(true);
      }
    };
    loadMasters();
  }, []);

  useEffect(
    () => () => {
      items.forEach((item) => item.media.forEach((m) => URL.revokeObjectURL(m.preview)));
    },
    [],
  );

  const item = items[activeIndex];

  const masterOptions = (list) =>
    (list || []).map((o) => ({ value: o?._id, label: o?.name || o?.code || '' }));
  const staticOptions = (list) => (list || []).map((o) => ({ value: o, label: t(o) }));

  const options = useMemo(
    () => {
      const categoryNameSet = new Set(CATEGORY_OPTIONS.map((name) => name.toLowerCase()));
      const categoryMasters = (masters.Category || []).filter((item) =>
        categoryNameSet.has(String(item?.name || item?.code || '').trim().toLowerCase()),
      );
      return {
        category: categoryMasters.length
          ? masterOptions(categoryMasters)
          : staticOptions(CATEGORY_OPTIONS),
        color: masterOptions(masters.Colour),
        clarity: masterOptions(masters.Clarity),
        cut: masterOptions(masters.Cut),
        location: masterOptions(masters.Location),
        lab: staticOptions(masters.GradedBy),
        weightUnit: staticOptions(masters.WUnit),
        priceUnit: staticOptions(masters.Unit),
        status: (masters.Status || []).length
          ? (masters.Status || []).map((s) => ({ value: String(s.value), label: t(s.label) }))
          : DEFAULT_STATUS_OPTIONS.map((s) => ({ value: String(s.value), label: t(s.label) })),
      };
    },
    [masters],
  );

  useEffect(() => {
    if (!isEdit || !mastersLoaded) return undefined;
    let cancelled = false;
    const loadDiamond = async () => {
      setEditLoading(true);
      try {
        const res = await _Api({ id: editId }, COLOR_STONE_DETAILS_API);
        if (cancelled) return;
        if (res?.code === 200 && res?.data && Number(res.data.Status) !== 1) {
          error(t('Only stock color stones can be edited'));
          navigate('/app/inventory/stoneSearch', { replace: true, state: { openColorStoneId: editId } });
        } else if (res?.code === 200 && res?.data) {
          const loaded = [detailsToItem(res.data)];
          initialSnapshotRef.current = snapshotItems(loaded);
          setItems(loaded);
          setActiveIndex(0);
        } else {
          error(apiErrorMessage(res) || t('Please Wait or try again later!!'));
        }
      } catch (err) {
        console.log(err, 'err');
      } finally {
        if (!cancelled) setEditLoading(false);
      }
    };
    loadDiamond();
    return () => {
      cancelled = true;
    };
  }, [isEdit, editId, mastersLoaded]);

  const detailsToItem = (d) => {
    const base = emptyItem();
    const rawCategoryList = Array.isArray(d.Category) ? d.Category : d.Category ? [d.Category] : [];
    const channel = Array.isArray(d.Channel) ? d.Channel : d.Channel ? [d.Channel] : [];
    const certificateList = Array.isArray(d.Certificates) && d.Certificates.length
      ? d.Certificates
      : d.GradedBy || d.Certification
        ? [{ GradedBy: d.GradedBy, Certification: d.Certification, CertificationUrl: d.CertificationUrl }]
        : [];
    const mediaUrl = (m) => (typeof m === 'string' ? m : m?.url || m?.image || m?.path || '');
    const images = (Array.isArray(d.image) ? d.image : [d.image]).map(mediaUrl).filter(Boolean);
    const videos = (Array.isArray(d.video) ? d.video : [d.video]).map(mediaUrl).filter(Boolean);
    const shapeKeys = [d.Shape_id, d.ShapeId, d.Shape, d.ShapeName, d.Shape_name, d.Shape?.name, d.Shape?._id]
      .filter((v) => !isBlank(v) && typeof v !== 'object')
      .map((v) => String(v).toLowerCase());
    const shapeMaster = (masters.Shape || []).find((s) =>
      [s?._id, s?.name, s?.code, s?.logo].some((v) => !isBlank(v) && shapeKeys.includes(String(v).toLowerCase())),
    );
    const shape = shapeMaster?._id || resolveOption(masterOptions(masters.Shape), d.Shape);
    const media = [
      ...images.map((url) => existingMedia(url, VIDEO_EXTENSIONS.includes(urlExtension(url)))),
      ...videos.map((url) => existingMedia(url, true)),
    ];
    const normalizeUrl = (url) => {
      let clean = String(url || '').split(/[?#]/)[0].trim();
      try {
        clean = decodeURIComponent(clean);
      } catch (e) {
      }
      return clean.toLowerCase();
    };
    const fileNameOf = (url) => normalizeUrl(url).split('/').pop();
    const defaultMediaUrl = mediaUrl(d.DefaultMedia);
    let defaultMediaEntry = null;
    if (defaultMediaUrl) {
      defaultMediaEntry =
        media.find((m) => normalizeUrl(m.existing) === normalizeUrl(defaultMediaUrl)) ||
        media.find((m) => fileNameOf(m.existing) === fileNameOf(defaultMediaUrl)) ||
        null;
      if (!defaultMediaEntry) {
        defaultMediaEntry = existingMedia(defaultMediaUrl, VIDEO_EXTENSIONS.includes(urlExtension(defaultMediaUrl)));
        media.unshift(defaultMediaEntry);
      }
    }
    const toText = (v) => (v === undefined || v === null ? '' : String(v));

    return {
      ...base,
      productId: toText(d.ProductID),
      productName: toText(d.ProductName),
      category: (() => {
        const categoryObj = d.Category && !Array.isArray(d.Category) && typeof d.Category === 'object'
          ? d.Category
          : null;
        const categoryCandidates = [
          categoryObj?.value,
          categoryObj?.key,
          categoryObj?.Category_name,
          categoryObj?.name,
          categoryObj?.label,
          d.DiamondCategory,
          typeof d.Category === 'string' ? d.Category : '',
          ...rawCategoryList.map((c) =>
            c && typeof c === 'object' ? c.value || c.key || c.Category_name || c.name || c.label : c,
          ),
        ];
        for (const candidate of categoryCandidates) {
          const matched = resolveOption(options.category, candidate);
          if (matched) return matched;
        }
        return '';
      })(),
      stoneName: toText(d.StoneName ?? d.stoneName),
      description: toText(d.Description),
      shape,
      size: toText(d.Size ?? d.size),
      color: resolveOption(options.color, d.Colour),
      clarity: resolveOption(options.clarity, d.Clarity),
      cut: resolveOption(options.cut, d.Cut),
      origin: toText(d.Origin),
      treatment: toText(d.Treatment ?? d.treatment),
      pcs: toText(d.Pcs),
      weight: toText(d.Carat),
      weightUnit: d.Unit || base.weightUnit,
      price: priceNumber(d.Price ?? d.price),
      priceUnit: d.PriceUnit || base.priceUnit,
      tagPrice: toText(d.TagPrice),
      lotNo: toText(d.LotNo),
      certificates: certificateList.length
        ? certificateList.map((c) => existingCertificate(c, options.lab))
        : [emptyCertificate()],
      status: d.Status !== undefined && d.Status !== null ? String(d.Status) : base.status,
      channelPos: channel.some((c) => String(c).toLowerCase() === 'pos'),
      channelEcommerce: channel.some((c) => String(c).toLowerCase() === 'ecommerce'),
      media,
      defaultMedia:
        defaultMediaEntry?.preview ||
        (d.DefaultMedia && media.find((m) => m.existing === d.DefaultMedia)?.preview) ||
        (String(d.DefaultMediaType).toLowerCase() === 'video' && media.find((m) => m.isVideo)?.preview) ||
        (d.DefaultImage && media.find((m) => m.existing === d.DefaultImage)?.preview) ||
        media[0]?.preview ||
        '',
      stockId: toText(d.StockID),
      location: resolveOption(options.location, d.Location),
    };
  };

  const shapeOptions = masterOptions(masters.Shape);

  const mediaRules = useMemo(() => {
    const image = {
      ...DEFAULT_MEDIA.image,
      ...(masters.media?.image || {}),
      extensions:
        masters.media?.image?.extensions?.length
          ? masters.media.image.extensions
          : DEFAULT_MEDIA.image.extensions,
    };
    const video = {
      ...DEFAULT_MEDIA.video,
      ...(masters.media?.video || {}),
      extensions:
        masters.media?.video?.extensions?.length
          ? masters.media.video.extensions
          : DEFAULT_MEDIA.video.extensions,
    };
    const imageExt = image.extensions || [];
    const videoExt = video.extensions || [];
    return {
      image,
      video,
      imageExt,
      videoExt,
      accept: [...imageExt, ...videoExt].map((ext) => `.${ext}`).join(','),
    };
  }, [masters]);

  const updateItemAt = (index, patch) =>
    setItems((prev) => prev.map((it, i) => (i === index ? { ...it, ...patch } : it)));

  const updateItem = (patch) => updateItemAt(activeIndex, patch);

  const labelOf = (list, value) =>
    (list || []).find((o) => String(o.value) === String(value))?.label || '';

  const diamondSpecs = (it) => ({
    carat: it.weight ? `${it.weight} ${it.weightUnit || ''}`.trim() : '',
    shape: labelOf(masterOptions(masters.Shape), it.shape),
    color: labelOf(options.color, it.color),
    clarity: labelOf(options.clarity, it.clarity),
    cut: labelOf(options.cut, it.cut),
    origin: it.origin,
    category: labelOf(options.category, it.category),
    lab: it.certificates.map((c) => labelOf(options.lab, c.lab)).filter(Boolean).join(', '),
  });

  const aiPayload = (it) => {
    const s = diamondSpecs(it);
    const details = {
      ProductName: it.productName.trim(),
      Category: s.category,
      Shape: s.shape,
      Carat: it.weight,
      Unit: it.weight ? it.weightUnit : '',
      Colour: s.color,
      Clarity: s.clarity,
      Cut: s.cut,
      Origin: s.origin,
      GradedBy: s.lab,
    };
    const filled = Object.fromEntries(Object.entries(details).filter(([, v]) => !isBlank(v)));
    return { ...filled, language: 'English' };
  };

  const AI_TARGETS = {
    productName: { api: AI_NAME_API, keys: ['name', 'productName', 'ProductName'], done: 'Product name generated' },
    description: { api: AI_DESCRIPTION_API, keys: ['description', 'Description'], done: 'Description Successfuly Generate' },
  };

  const generateWithAi = async (field) => {
    if (aiInactive || generating) return;
    const index = activeIndex;
    const payload = aiPayload(items[index]);
    if (Object.keys(payload).length === 1) {
      error(t('Select diamond details first'));
      return;
    }
    const target = AI_TARGETS[field];
    setGenerating({ index, field });
    try {
      const res = await _Api(payload, target.api);
      if (res?.code === 200) {
        const data = res?.data;
        const text =
          typeof data === 'string' ? data : target.keys.map((k) => data?.[k]).find(Boolean) || '';
        updateItemAt(index, { [field]: String(text).trim() });
        success(t(target.done));
      } else {
        error(t(res?.message || res?.errors || 'Please Wait or try again later!!'));
      }
    } catch (err) {
      console.log(err, 'err');
    } finally {
      setGenerating(null);
    }
  };

  const isGenerating = (field) => generating?.index === activeIndex && generating?.field === field;

  const selectShape = (shapeId) => {
    updateItem({ shape: shapeId });
  };

  const itemErrors = showErrors ? getItemErrors(item, t) : null;

  const bind = (key) => ({
    value: item[key],
    onChange: (v) => updateItem({ [key]: v }),
    error: itemErrors?.fields[key],
  });

  const updateCertificate = (index, patch) =>
    updateItem({
      certificates: item.certificates.map((c, i) => (i === index ? { ...c, ...patch } : c)),
    });

  const setCertificateFile = (index, file) => {
    const previous = item.certificates[index]?.preview;
    if (previous) URL.revokeObjectURL(previous);
    updateCertificate(index, {
      file,
      preview: file && file.type.startsWith('image/') ? URL.createObjectURL(file) : '',
    });
  };

  const addTab = () => {
    const base = emptyItem();
    const defaultStatus = masters.Status?.[0]?.value ?? DEFAULT_STATUS_OPTIONS[0].value;
    const next = {
      ...base,
      status: defaultStatus !== undefined ? String(defaultStatus) : base.status || DEFAULT_STATUS_OPTIONS[0].value,
      weightUnit: masters.Unit?.[0] || base.weightUnit,
      priceUnit: masters.PriceUnit?.[0] || base.priceUnit,
    };
    setItems((prev) => [...prev, next]);
    setActiveIndex(items.length);
  };

  const closeTab = (index) => {
    if (items.length === 1) return;
    items[index].media.forEach((m) => URL.revokeObjectURL(m.preview));
    setItems((prev) => prev.filter((_, i) => i !== index));
    setActiveIndex((current) => (current >= index && current > 0 ? current - 1 : current));
  };

  const addMedia = (fileList) => {
    const { image, video, imageExt, videoExt } = mediaRules;
    let imageCount = item.media.filter((m) => !m.isVideo).length;
    let videoCount = item.media.filter((m) => m.isVideo).length;
    const added = [];
    const rejected = [];
    Array.from(fileList || []).forEach((file) => {
      const ext = fileExtension(file);
      const isVideo = videoExt.includes(ext);
      const rule = isVideo ? video : image;
      const sizeMB = file.size / (1024 * 1024);
      if (!isVideo && !imageExt.includes(ext)) {
        rejected.push(`${file.name}: ${t('unsupported format')}`);
      } else if (rule.maxSizeMB && sizeMB > rule.maxSizeMB) {
        rejected.push(`${file.name}: ${t('max size')} ${rule.maxSizeMB}MB`);
      } else if (rule.maxFiles && (isVideo ? videoCount : imageCount) >= rule.maxFiles) {
        rejected.push(`${file.name}: ${t('max files')} ${rule.maxFiles}`);
      } else {
        if (isVideo) videoCount += 1;
        else imageCount += 1;
        added.push({ file, preview: URL.createObjectURL(file), isVideo, isGif: ext === 'gif' });
      }
    });
    if (rejected.length) error(rejected.join('\n'));
    if (added.length) updateItem({ media: [...item.media, ...added] });
  };

  const removeMedia = (index) => {
    const removed = item.media[index];
    URL.revokeObjectURL(removed.preview);
    updateItem({
      media: item.media.filter((_, i) => i !== index),
      defaultMedia: item.defaultMedia === removed.preview ? '' : item.defaultMedia,
    });
  };

  const defaultMediaOf = (it) =>
    it.media.find((m) => m.preview === it.defaultMedia) || it.media[0] || null;

  const orderedMedia = (it) => {
    const first = defaultMediaOf(it);
    return first ? [first, ...it.media.filter((m) => m !== first)] : [];
  };

  const canSave = !saving && items.some(isItemTouched);
  const previewMedia = defaultMediaOf(item);

  const hasUnsavedChanges = isEdit
    ? initialSnapshotRef.current !== null && snapshotItems(items) !== initialSnapshotRef.current
    : items.some(isItemTouched);

  const leavePage = () => {
    setDiscardOpen(false);
    navigate('/app/inventory/stoneSearch', isEdit ? { state: { openColorStoneId: editId } } : undefined);
  };

  const requestLeave = (e, type = 'cancel') => {
    e?.preventDefault?.();
    if (hasUnsavedChanges && !saving) setDiscardOpen({ type });
    else leavePage();
  };

  const requestCloseTab = (index) => {
    if (isItemTouched(items[index])) setDiscardOpen({ type: 'tab', index });
    else closeTab(index);
  };

  const discardType = discardOpen?.type || 'cancel';

  const confirmDiscard = () => {
    if (discardType === 'tab') {
      closeTab(discardOpen.index);
      setDiscardOpen(false);
    } else {
      leavePage();
    }
  };

  const currencyFor = (it) =>
    (masters.Location || []).find((l) => l?._id === it.location)?.currency ||
    masters.Location?.[0]?.currency ||
    '';

  const buildPayload = async (it) => {
    const media = orderedMedia(it);
    const previewGif = media[0]?.isGif ? media[0] : null;
    const isImageEntry = (m) => !m.isVideo || m === previewGif;
    const images = await Promise.all(
      media.filter(isImageEntry).map((m) => m.existing || readFileAsDataUrl(m.file)),
    );
    const videos = await Promise.all(
      media.filter((m) => !isImageEntry(m)).map((m) => m.existing || readFileAsDataUrl(m.file)),
    );
    const certificates = await Promise.all(
      it.certificates.map(async (c) => ({
        GradedBy: c.lab,
        Certification: c.number.trim(),
        CertificationUrl: c.url.trim(),
        image: c.file ? c.file.existing || (await readFileAsDataUrl(c.file)) : '',
      })),
    );
    const channel = [];
    if (it.channelPos) channel.push('POS');
    if (it.channelEcommerce) channel.push('eCommerce');

    return {
      StockID: it.stockId.trim(),
      ProductID: it.productId.trim(),
      ProductName: it.productName.trim(),
      Category: (() => {
        if (!it.category) return null;
        const opt = (options.category || []).find((o) => String(o.value) === String(it.category));
        const label = opt
          ? (/^[a-f\d]{24}$/i.test(String(opt.value)) ? String(opt.label || '') : String(opt.value))
          : String(it.category);
        return label ? { key: label, value: label } : null;
      })(),
      StoneName: (it.stoneName || '').trim(),
      Description: it.description,
      Channel: channel,
      Location: it.location,
      Shape: it.shape,
      Colour: it.color,
      Clarity: it.clarity,
      Cut: it.cut,
      Origin: it.origin,
      Treatment: it.treatment,
      Size: it.size === '' ? '' : Number(it.size),
      Pcs: Number(it.pcs),
      Carat: Number(it.weight),
      Unit: it.weightUnit,
      Price: it.price === '' ? 0 : Number(it.price),
      PriceUnit: it.priceUnit,
      Amount: calcAmount(it),
      Curr: currencyFor(it),
      image: images,
      video: videos,
      DefaultMedia: media[0] ? (isImageEntry(media[0]) ? images[0] : videos[0]) : '',
      Certificates: certificates,
    };
  };

  const handleSave = async () => {
    const invalidIndex = items.findIndex((it) => !isItemValid(it));
    if (invalidIndex !== -1) {
      setShowErrors(true);
      setActiveIndex(invalidIndex);
      error(t('Please fill all required fields'));
      return;
    }

    if (isEdit) {
      setSaving(true);
      const loadingId = loading();
      try {
        const res = await _Api({ id: editId, ...(await buildPayload(items[0])) }, EDIT_COLOR_STONE_API);
        if (res?.code === 200) {
          Updateloading(t, loadingId, t('Color stone updated successfully'));
          navigate('/app/inventory/stoneSearch', { state: { openColorStoneId: editId } });
        } else {
          Updateloading(t, loadingId, apiErrorMessage(res) || t('Please Wait or try again later!!'), 'error');
        }
      } catch (err) {
        console.log(err, 'err');
        Updateloading(t, loadingId, err?.message || t('Please Wait or try again later!!'), 'error');
      }
      setSaving(false);
      return;
    }

    setSaving(true);
    const loadingId = loading();
    const savedIndexes = [];
    let failedMessage = '';
    for (let index = 0; index < items.length; index += 1) {
      try {
        const res = await _Api(await buildPayload(items[index]), CREATE_COLOR_STONE_API);
        if (res?.code === 200) {
          savedIndexes.push(index);
        } else {
          const message = apiErrorMessage(res) || t('Please Wait or try again later!!');
          failedMessage =
            items.length > 1 ? `${t('Stock Items')} ${index + 1}: ${message}` : message;
          setActiveIndex(index);
          break;
        }
      } catch (err) {
        console.log(err, 'err');
        failedMessage = err?.message || t('Please Wait or try again later!!');
        setActiveIndex(index);
        break;
      }
    }

    if (!failedMessage) {
      Updateloading(t, loadingId, t('Color stones created successfully'));
      navigate('/app/inventory/stoneSearch');
    } else {
      Updateloading(t, loadingId, failedMessage, 'error');
      if (savedIndexes.length) {
        setItems((prev) => prev.filter((_, i) => !savedIndexes.includes(i)));
        setActiveIndex(0);
      }
    }
    setSaving(false);
  };

  const currency = currencyFor(item);
  const amount = calcAmount(item);

  const allIndexes = items.map((_, index) => index);
  const visibleTabs =
    items.length <= MAX_VISIBLE_TABS
      ? allIndexes
      : activeIndex < MAX_VISIBLE_TABS
        ? allIndexes.slice(0, MAX_VISIBLE_TABS)
        : [...allIndexes.slice(0, MAX_VISIBLE_TABS - 1), activeIndex];
  const overflowTabs = allIndexes.filter((index) => !visibleTabs.includes(index));

  const renderTab = (index, inMenu = false) => (
    <div
      key={index}
      role="tab"
      tabIndex={0}
      aria-selected={index === activeIndex}
      className={`dc-tab ${index === activeIndex ? 'active' : ''} ${inMenu ? 'in-menu' : ''}`}
      onClick={() => {
        setActiveIndex(index);
        setMoreTabsOpen(false);
      }}
      onKeyDown={(e) => e.key === 'Enter' && setActiveIndex(index)}
    >
      {showErrors && !isItemValid(items[index]) && <span className="dc-tab-dot" />}
      <span className="dc-tab-label">
        {t('Stock Items')} {index + 1}
      </span>
      {items.length > 1 && (
        <button
          type="button"
          className="dc-tab-close"
          aria-label={t('Close')}
          onClick={(e) => {
            e.stopPropagation();
            requestCloseTab(index);
          }}
        >
          <CloseIcon fontSize="inherit" />
        </button>
      )}
    </div>
  );

  return (
    <div className="main-body dc-page">
      <div className="dc-header">
        <nav className="dc-breadcrumb">
          
          
          <Link to="/app/inventory/stoneSearch" onClick={(e) => requestLeave(e, 'leave')}>
            {t('Color Stone')}
          </Link>
          
          <ArrowForwardIosIcon fontSize="small" htmlColor='#BCBCBC' />
          
          <span className="dc-breadcrumb-current">
            {isEdit ? t('Edit Color Stone') : t('Create Color Stone')}
          </span>
        </nav>
        <div className="dc-tabs" role="tablist">
          {visibleTabs.map((index) => renderTab(index))}
          {overflowTabs.length > 0 && (
            <div className="dc-tab-more" ref={moreTabsRef}>
              <button
                type="button"
                className={`dc-tab ${moreTabsOpen ? 'open' : ''}`}
                onClick={() => setMoreTabsOpen((o) => !o)}
                style={{border:0}}
              >
                <span className="dc-tab-label">
                  {t('More')} + {overflowTabs.length}
                </span>
                <MoreVertIcon fontSize="inherit" className="dc-tab-more-icon" />
              </button>
              {moreTabsOpen && (
                <ul className="dc-tab-menu">
                  {overflowTabs.map((index) => (
                    <li key={index}>{renderTab(index, true)}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
          
          {!isEdit && (
            <button type="button" className="dc-tab-add" onClick={addTab} aria-label={t('Add')}>
              <AddIcon fontSize="small" />
            </button>
          )}
        </div>
      </div>

      {editLoading && <p className="dc-edit-loading">{t('Loading...')}</p>}
      <div className="dc-body" style={editLoading ? { display: 'none' } : undefined}>
        <div className="dc-main dc-card">
          <section className="dc-section">
            <h6 className="dc-section-title">{t('General Information')}</h6>
            <div className="dc-grid cols-3">
              <TextField label={t('Product ID')} required {...bind('productId')} />
              <TextField
                label={t('Product Name')}
                required
                icon={
                  isGenerating('productName') ? (
                    <span className="dc-ai-loader" aria-label={t('Generating')} />
                  ) : (
                    <button
                      type="button"
                      className={`dc-ai-btn ${aiInactive ? 'is-inactive' : ''}`}
                      aria-disabled={aiInactive || undefined}
                      onClick={(e) => {
                        e.preventDefault();
                        generateWithAi('productName');
                      }}
                    >
                      <AutoAwesomeOutlinedIcon fontSize="inherit" />
                    </button>
                  )
                }
                {...bind('productName')}
              />
              <SelectField
                label={t('Category')}
                required
                options={options.category}
                {...bind('category')}
              />
            </div>
            <label className="dc-textarea">
              {isGenerating('description') ? (
                <span className="dc-ai-loader" aria-label={t('Generating')} />
              ) : (
                <button
                  type="button"
                  className={`dc-ai-btn ${aiInactive ? 'is-inactive' : ''}`}
                  aria-disabled={aiInactive || undefined}
                  onClick={(e) => {
                    e.preventDefault();
                    generateWithAi('description');
                  }}
                >
                  <AutoAwesomeOutlinedIcon fontSize="inherit" />
                </button>
              )}
              <textarea
                rows={4}
                placeholder={t('Description will appear here...')}
                value={item.description}
                onChange={(e) => updateItem({ description: e.target.value })}
              />
            </label>
          </section>

          <section className="dc-section">
            <h6 className="dc-section-title">{t('Stones Details')}</h6>
            <div className="dc-grid cols-3">
              
              <TextField label={t('Stone Name')} {...bind('stoneName')} />
              <SelectField
                label={t('Shape')}
                required
                options={shapeOptions}
                value={item.shape}
                onChange={selectShape}
                error={itemErrors?.fields.shape}
              />
              <TextField label={t('Size (ct)')} type="number" {...bind('size')} />
              <SelectField label={t('Color')} required options={options.color} {...bind('color')} />
              <SelectField label={t('Clarity')} required options={options.clarity} {...bind('clarity')} />
              <SelectField label={t('Cut')} required options={options.cut} {...bind('cut')} />
              <SelectField
                label={t('Origin')}
                required
                options={originOptions}
                {...bind('origin')}
              />
              <TextField
                label={t('Treatment')}
                type="text"
                {...bind('treatment')}
              />
             
            </div>
          </section>

          <section className="dc-section">
            <h6 className="dc-section-title">{t('Quantity')}</h6>
            <div className="dc-quantity">
              <TextField label={t('Pcs')} required type="number" {...bind('pcs')} />
              <div className="dc-with-unit">
                <TextField label={t('Weight')} required type="number" {...bind('weight')} />
                <SelectField
                  label={t('WUnit')}
                  compact
                  options={options.weightUnit}
                  {...bind('weightUnit')}
                />
              </div>
              <div className="dc-with-unit">
                <TextField label={t('Price')} required type="number" {...bind('price')} />
                <SelectField
                  label={t('Unit')}
                  compact
                  options={options.priceUnit}
                  {...bind('priceUnit')}
                />
              </div>
              <TextField
                label={t('Amount')}
                disabled
                alwaysFloat
                value={`${currency} ${amount.toFixed(2)}`.trim()}
              />
            </div>
          </section>

          <section className="dc-section">
            <h6 className="dc-section-title">{t('Certificate')}</h6>
            {item.certificates.map((cert, index) => (
              <div className="dc-cert" key={index}>
                
                <input
                  type="file"
                  hidden
                  accept="application/pdf,image/*"
                  ref={(el) => {
                    certFileRefs.current[index] = el;
                  }}
                  onChange={(e) => {
                    setCertificateFile(index, e.target.files?.[0] || null);
                    e.target.value = '';
                  }}
                />
                <div className="dc-cert-row">
                  <SelectField
                    label={t('Certificate')}
                    required
                    openUp
                    options={options.lab}
                    value={cert.lab}
                    onChange={(v) => updateCertificate(index, { lab: v })}
                    error={itemErrors?.certificates[index]?.lab}
                  />
                  <TextField
                    label={t('Certificate No.')}
                    required
                    value={cert.number}
                    onChange={(v) => updateCertificate(index, { number: v })}
                    error={itemErrors?.certificates[index]?.number}
                  />
                  
                  {!cert.file && (
                    <button
                      type="button"
                      className="dc-cert-tool"
                      onClick={() => certFileRefs.current[index]?.click()}
                    >
                      
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8.13143 17.9562L14.1705 12.2724L17.0124 15.1143M8.13143 17.9562H15.2362C16.4133 17.9562 17.3676 17.0019 17.3676 15.8248V12.2724M8.13143 17.9562C6.95427 17.9562 6 17.0019 6 15.8248V8.72C6 7.54285 6.95427 6.58858 8.13143 6.58858H12.7495M16.6571 10.0191L16.6571 8.00953M16.6571 8.00953L16.6571 6M16.6571 8.00953L14.6476 8.00953M16.6571 8.00953L18.6667 8.00953M10.2629 9.78572C10.2629 10.3743 9.78572 10.8514 9.19714 10.8514C8.60856 10.8514 8.13143 10.3743 8.13143 9.78572C8.13143 9.19714 8.60856 8.72 9.19714 8.72C9.78572 8.72 10.2629 9.19714 10.2629 9.78572Z" stroke="#606060" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  )}
                  {cert.file && (
                    <span className="dc-cert-thumb">
                      {cert.preview ? (
                        <img src={cert.preview} alt={cert.file.name} />
                      ) : (
                        <span className="dc-cert-thumb-doc">PDF</span>
                      )}
                      <button
                        type="button"
                        className="dc-cert-badge"
                        aria-label={t('Remove')}
                        onClick={() => setCertificateFile(index, null)}
                      >
                        <CloseIcon fontSize="inherit" />
                      </button>
                    </span>
                  )}
                  {!(cert.showUrl || itemErrors?.certificates[index]?.url) && (
                    <button
                      type="button"
                      className={`dc-cert-tool ${cert.showUrl ? 'active' : ''}`}
                      onClick={() => updateCertificate(index, { showUrl: true })}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M13.219 10.781C12.1776 9.73965 10.4891 9.73965 9.44772 10.781L6.78105 13.4477C5.73965 14.4891 5.73965 16.1776 6.78105 17.219C7.82245 18.2603 9.51089 18.2603 10.5523 17.219L11.2867 16.4846M10.781 13.219C11.8224 14.2603 13.5109 14.2604 14.5523 13.219L17.219 10.5523C18.2603 9.51089 18.2603 7.82245 17.219 6.78105C16.1776 5.73965 14.4891 5.73965 13.4477 6.78105L12.7146 7.51412" stroke="#606060" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  )}
                  {(cert.showUrl || itemErrors?.certificates[index]?.url) && (
                    <span
                      className={`dc-cert-link ${itemErrors?.certificates[index]?.url ? 'has-error' : ''}`}
                    >
                      <input
                        type="text"
                        value={cert.url}
                        placeholder="https://......com/"
                        onChange={(e) => updateCertificate(index, { url: e.target.value })}
                      />
                      <button
                        type="button"
                        className="dc-cert-badge"
                        aria-label={t('Remove')}
                        onClick={() => updateCertificate(index, { url: '', showUrl: false })}
                      >
                        <CloseIcon fontSize="inherit" />
                      </button>
                    </span>
                  )}
                  <button
                    type="button"
                    className="dc-icon-btn"
                    disabled={item.certificates.length === 1}
                    onClick={() =>
                      updateItem({ certificates: item.certificates.filter((_, i) => i !== index) })
                    }
                  >
                    
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M3 4.63235L15 4.63235M7.5 12.5735V7.80882M10.5 12.5735V7.80882M12 15.75L6 15.75C5.17157 15.75 4.5 15.0389 4.5 14.1618L4.5 5.42647C4.5 4.98789 4.83579 4.63235 5.25 4.63235L12.75 4.63235C13.1642 4.63235 13.5 4.98789 13.5 5.42647L13.5 14.1618C13.5 15.0389 12.8284 15.75 12 15.75ZM7.5 4.63235L10.5 4.63235C10.9142 4.63235 11.25 4.27681 11.25 3.83824V3.04412C11.25 2.60554 10.9142 2.25 10.5 2.25L7.5 2.25C7.08579 2.25 6.75 2.60554 6.75 3.04412V3.83824C6.75 4.27681 7.08579 4.63235 7.5 4.63235Z" stroke="#606060" stroke-linecap="round" stroke-linejoin="round"/>
</svg>

                  </button>
                </div>
                
              </div>
            ))}
            <button
              type="button"
              className="dc-add-link"
              onClick={() => updateItem({ certificates: [...item.certificates, emptyCertificate()] })}
            >
              <AddIcon fontSize="medium" />
              {t('Add Certificate')}
            </button>
          </section>
        </div>

        <aside className="dc-side">
          <div className="dc-card dc-grid cols-2">
            <div>
              <h6 className="dc-section-title">{t('Status')}</h6>
              <SelectField
                label={t('Status')}
                compact
                options={options.status}
                {...bind('status')}
              />
            </div>
            <div>
              <h6 className="dc-section-title">{t('Channel')}</h6>
              <div className="dc-checks">
                <label className="dc-check">
                  <input
                    type="checkbox"
                    checked={item.channelPos}
                    onChange={(e) => updateItem({ channelPos: e.target.checked })}
                  />
                  <span />
                  {t('Point of Sale')}
                </label>
                <label className="dc-check">
                  <input
                    type="checkbox"
                    checked={item.channelEcommerce}
                    onChange={(e) => updateItem({ channelEcommerce: e.target.checked })}
                  />
                  <span />
                  {t('eCommerce')}
                </label>
              </div>
            </div>
          </div>

          <div className="dc-card">
            <h6 className="dc-section-title">{t('Media (images / video)')}</h6>
            <input
              ref={mediaInputRef}
              type="file"
              hidden
              multiple
              accept={mediaRules.accept}
              onChange={(e) => {
                addMedia(e.target.files);
                e.target.value = '';
              }}
            />
            <div
              className="dc-media"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                addMedia(e.dataTransfer.files);
              }}
            >
              <div className="dc-media-preview">
                <span className="dc-checker" />
                <div className="dc-media-stage">
                  {previewMedia ? (
                    previewMedia.isVideo && !previewMedia.isGif ? (
                      <video
                        key={previewMedia.preview}
                        src={previewMedia.preview}
                        controls
                        autoPlay
                        muted
                        loop
                        playsInline
                      />
                    ) : (
                      <img src={previewMedia.preview} alt="" />
                    )
                  ) : (
                    <VideoLibraryOutlinedIcon className="dc-media-empty-icon" />
                  )}
                </div>
                <span className="dc-checker" />
              </div>
              <p className="dc-media-hint">
                {`${t('Supports image formats')}: ${mediaRules.imageExt.join(', ').toUpperCase()} ${t('and video formats')}: ${mediaRules.videoExt.join(', ').toUpperCase()}`}
              </p>
              <div className="dc-media-thumbs">
                {item.media.map((m, index) => {
                  const isDefault = m === previewMedia;
                  return (
                    <div
                      className={`dc-media-thumb ${isDefault ? 'is-default' : ''}`}
                      key={m.preview}
                      role="button"
                      tabIndex={0}
                      onClick={() => updateItem({ defaultMedia: m.preview })}
                      onKeyDown={(e) => e.key === 'Enter' && updateItem({ defaultMedia: m.preview })}
                    >
                      {m.isVideo && !m.isGif ? (
                        <video src={m.preview} muted />
                      ) : (
                        <img src={m.preview} alt="" />
                      )}
                      <button
                        type="button"
                        className="dc-media-remove"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeMedia(index);
                        }}
                        aria-label={t('Remove')}
                      >
                        <CloseIcon fontSize="inherit" />
                      </button>
                      <span
                        className={`dc-media-radio ${isDefault ? 'checked' : ''}`}
                        role="radio"
                        aria-checked={isDefault}
                        aria-label={t('Set as default')}
                      />
                    </div>
                  );
                })}
                <button
                  type="button"
                  className="dc-media-add"
                  onClick={() => mediaInputRef.current?.click()}
                  aria-label={t('Add media')}
                >
                  {item.media.length ? <AddIcon /> : <AddPhotoAlternateOutlinedIcon />}
                </button>
              </div>
            </div>
          </div>
<div className="dc-card ">
          <div className="dc-grid cols-2">
            <div>
              <h6 className="dc-section-title">{t('Stock ID')}</h6>
              <TextField label={t('Stock ID')} required  {...bind('stockId')} />
            </div>
            <div>
              <h6 className="dc-section-title">{t('Location')}</h6>
              <SelectField label={t('Location')} required options={options.location} {...bind('location')} />
            </div>
          </div>
            </div>
        </aside>
      </div>

      <div className="dc-footer">
        <button
          type="button"
          className="dc-btn-cancel"
          onClick={(e) => requestLeave(e, 'cancel')}
        >
          {t('Cancel')}
        </button>
        <button
          type="button"
          className="dc-btn-save"
          disabled={!canSave || editLoading}
          onClick={handleSave}
        >
          {t('Save')}
        </button>
      </div>
      {discardOpen && (
        <div className="dc-discard-backdrop" onMouseDown={() => setDiscardOpen(false)}>
          <div
            className="dc-discard-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="dc-discard-title"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <h6 className="dc-discard-title" id="dc-discard-title">
            <svg width="32" height="24" viewBox="0 0 32 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M10.0306 3.65709C10.8866 2.10909 13.1116 2.10909 13.9676 3.65709L21.7136 17.6581C22.5436 19.1581 21.4586 20.9981 19.7446 20.9981H4.25456C2.53956 20.9981 1.45456 19.1581 2.28456 17.6581L10.0306 3.65709ZM12.9976 16.9981C13.0018 16.8643 12.9791 16.731 12.9308 16.6062C12.8826 16.4814 12.8097 16.3675 12.7165 16.2714C12.6234 16.1752 12.5119 16.0988 12.3886 16.0466C12.2654 15.9944 12.1329 15.9675 11.9991 15.9675C11.8652 15.9675 11.7327 15.9944 11.6095 16.0466C11.4862 16.0988 11.3747 16.1752 11.2816 16.2714C11.1884 16.3675 11.1156 16.4814 11.0673 16.6062C11.019 16.731 10.9963 16.8643 11.0006 16.9981C11.0088 17.2574 11.1176 17.5034 11.3039 17.684C11.4903 17.8645 11.7396 17.9655 11.9991 17.9655C12.2585 17.9655 12.5078 17.8645 12.6942 17.684C12.8806 17.5034 12.9894 17.2574 12.9976 16.9981ZM12.7386 9.14509C12.7126 8.95676 12.6162 8.78529 12.4687 8.66537C12.3212 8.54544 12.1336 8.48602 11.9439 8.4991C11.7543 8.51218 11.5767 8.5968 11.447 8.73585C11.3174 8.87489 11.2454 9.05798 11.2456 9.24809L11.2496 13.7491L11.2566 13.8511C11.2825 14.0394 11.3789 14.2109 11.5265 14.3308C11.674 14.4507 11.8615 14.5102 12.0512 14.4971C12.2408 14.484 12.4185 14.3994 12.5481 14.2603C12.6777 14.1213 12.7498 13.9382 12.7496 13.7481L12.7456 9.24609L12.7386 9.14509Z" fill="#FABB2D"/>
</svg>

              {t('Discard Changes')}
            </h6>
            <p className="dc-discard-text">
              {t('You have unsaved changes.')}
              <br />
              {t('If you leave this page, your changes will be lost.')}
            </p>
            <p className="dc-discard-text">{t('Do you want to continue?')}</p>
            <div className="dc-discard-actions">
              
              <button type="button" className="dc-discard-cancel" onClick={() => setDiscardOpen(false)}>
                {discardType === 'cancel' ? t('Cancel') : t('Stay')}
              </button>
              <button type="button" className="dc-discard-confirm" onClick={confirmDiscard} autoFocus>
                {discardType === 'cancel' ? t('Discard') : t('Leave')}
              </button>
            </div>
          </div>
        </div>
      )}
      <GetCountrygraph setCountrynamearr={setCountrynamearr} />
    </div>
  );
};

export default CreateColorStone;

